import { cloneDeep } from 'lodash';

import { convertTreeToOrder } from '../../../utils/driver-tree';
import { NonFinancialDriverNodeList } from '../../../../core/api/types/non-financial-drivers';
import { NonFinancialDriverTree, NonFinancialDriverTreeNode } from '../../../types';

/**
 * Recursively finds a node by ID in the tree
 */
const findNode = (nodeId: string, tree: NonFinancialDriverTreeNode[]): NonFinancialDriverTreeNode | null => {
  for (const node of tree) {
    if (node.id === nodeId) {
      return node;
    }
    if (node.children.length > 0) {
      const found = findNode(nodeId, node.children);
      if (found) {
        return found;
      }
    }
  }
  return null;
};

/**
 * Recursively removes a node from the tree
 */
const removeNode = (nodeId: string, tree: NonFinancialDriverTreeNode[]): boolean => {
  for (let i = 0; i < tree.length; i++) {
    if (tree[i].id === nodeId) {
      tree.splice(i, 1);
      return true;
    }
    if (tree[i].children.length > 0) {
      if (removeNode(nodeId, tree[i].children)) {
        return true;
      }
    }
  }
  return false;
};

/**
 * Builds the driver ordering payload for updating group membership.
 * This function:
 * 1. Clones the current driver tree
 * 2. Moves any drivers being removed from the group to root level
 * 3. Removes selected drivers from their current positions
 * 4. Updates the group with the selected drivers as children
 * 5. Converts the tree back to order format
 *
 * @param groupId - The ID of the group being edited
 * @param selectedDrivers - Array of drivers that should belong to the group
 * @param driverTree - The current driver tree structure
 * @returns The ordering payload ready for the API
 */
export const buildDriverGroupOrderingPayload = (
  groupId: string,
  selectedDrivers: NonFinancialDriverTreeNode[],
  driverTree: NonFinancialDriverTree
): NonFinancialDriverNodeList[] => {
  // Clone the tree to avoid mutating the original
  const draftTree = cloneDeep(driverTree.tree);

  // Find the group node
  const groupNode = findNode(groupId, draftTree);
  if (!groupNode) {
    throw new Error(`Group with ID ${groupId} not found in tree`);
  }

  // Find drivers that are currently in the group but NOT in the selected list
  // These need to be moved to root level
  const selectedIds = new Set(selectedDrivers.map(d => d.id));
  const driversToRemoveFromGroup = groupNode.children.filter(child => !selectedIds.has(child.id));

  // Move removed drivers to root level
  driversToRemoveFromGroup.forEach(driver => {
    const driverClone = cloneDeep(driver);
    driverClone.parentId = undefined;
    driverClone.children = []; // Groups can't contain other groups
    draftTree.push(driverClone);
  });

  // Collect drivers to move into the group (cloning them before removal)
  const driversToMove: NonFinancialDriverTreeNode[] = [];
  selectedDrivers.forEach(driver => {
    const foundDriver = findNode(driver.id, draftTree);
    if (foundDriver) {
      driversToMove.push(cloneDeep(foundDriver));
    }
  });

  // Remove selected drivers from their current positions
  selectedDrivers.forEach(driver => {
    removeNode(driver.id, draftTree);
  });

  // Update group's children and set parent IDs
  groupNode.children = driversToMove.map(driver => ({
    ...driver,
    parentId: groupId,
    children: [] // Groups can't contain other groups, so clear children
  }));

  // Convert the modified tree back to order format
  return convertTreeToOrder(draftTree);
};
